import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Phone,
    Mail,
    MapPin,
    Send,
    Loader2,
    Compass,
    HardHat,
    Users,
    ClipboardCheck,
    CheckCircle2,
    Plus,
    Minus,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import useFAQSchema from "../hooks/useFAQSchema";
import { FAQ_CONTACT } from "../data/faqs";
import { site } from "../data/site";

/* ──────────────────────────────────────────────────────────────────
 *  /contact — Tell us about your roof.
 *  The enquiry form is the single conversion flow. Supporting
 *  information (phone / email / service area / why SRM) sits below.
 *  No hero CTAs, no final CTA — one action per page.
 * ────────────────────────────────────────────────────────────────── */

const EYEBROW =
    "font-display font-bold tracking-[0.26em] uppercase text-srm-gold text-[12px] lg:text-[13px]";
const GOLD_RULE = "block h-[3px] w-16 bg-srm-gold";

const PHONES = site?.business?.phones || [];
const PRIMARY_PHONE = PHONES[0]?.number || "";
const PRIMARY_PHONE_HREF = `tel:${PRIMARY_PHONE.replace(/\s+/g, "")}`;
const EMAIL = site?.business?.email || "info@strataroofmanagement.com.au";
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

/* Serialise FormData into the application/x-www-form-urlencoded body
   Netlify Forms expects when no file inputs are present. */
function encodeFormData(fd) {
    const params = new URLSearchParams();
    for (const [k, v] of fd.entries()) {
        params.append(k, typeof v === "string" ? v : "");
    }
    return params.toString();
}

/* === 1. Enquiry form — the primary content ======================== */
function ContactForm() {
    const ref = useReveal();
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        company: "",
        email: "",
        phone: "",
        address: "",
        message: "",
    });

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please complete your name, email and a short message.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            toast.error("Please provide a valid email address.");
            return;
        }

        setSubmitting(true);
        try {
            /* Netlify Forms native submission: URL-encoded POST to "/"
               with a matching "form-name" field. */
            const fd = new FormData(e.currentTarget);
            fd.set("form-name", "srm-enquiry");

            const resp = await fetch("/", {
                method: "POST",
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: encodeFormData(fd),
            });
            if (!resp.ok) throw new Error(`Netlify submission failed (${resp.status})`);

            setSubmitted(true);
            toast.success("Enquiry received. We'll be in touch shortly.");
            console.info("[SRM enquiry] captured by Netlify Forms");

            /* Best-effort secondary email notification via existing backend.
               Any failure here MUST NOT invalidate the already-captured
               Netlify submission — swallow silently. */
            try {
                await axios.post(
                    `${API}/enquiries`,
                    {
                        name: form.name.trim(),
                        email: form.email.trim(),
                        phone: form.phone.trim() || undefined,
                        organisation: form.company.trim() || undefined,
                        location: form.address.trim() || undefined,
                        message: form.message.trim(),
                    },
                    { headers: { "Content-Type": "application/json" }, timeout: 20000 }
                );
            } catch (_e) {
                /* Optional email path failed — Netlify already has the enquiry. */
            }
        } catch (err) {
            toast.error(
                "We couldn't submit your enquiry right now. Please try again in a moment, or email info@strataroofmanagement.com.au directly."
            );
            console.error("[SRM enquiry] submission failed:", err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            ref={ref}
            id="enquiry-form"
            data-testid="contact-form-section"
            className="bg-white scroll-mt-24"
        >
            <div className="container-srm py-20 lg:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-[42fr_58fr] gap-12 lg:gap-20">
                    <div className="reveal">
                        <div className={EYEBROW}>Contact SRM</div>
                        <span aria-hidden className={`mt-5 ${GOLD_RULE}`} />
                        <h1
                            data-testid="contact-hero-headline"
                            className="mt-8 font-display font-bold text-[2.1rem] sm:text-[2.5rem] lg:text-[2.95rem] tracking-[-0.024em] leading-[1.1] text-srm-navy text-balance"
                        >
                            Tell us about your property.
                        </h1>
                        <p className="mt-8 text-[15.5px] lg:text-[17px] text-srm-body leading-[1.85] max-w-[480px]">
                            Tell us a little about your property or roofing concern and
                            one of our team will be in touch to discuss the most
                            appropriate next step.
                        </p>
                        <p className="mt-5 text-[14px] lg:text-[14.5px] text-srm-body/85 leading-[1.75] max-w-[480px] italic">
                            Our team can request supporting photographs after
                            reviewing your enquiry.
                        </p>

                        <ul className="mt-12 space-y-5">
                            {[
                                "An independent assessment that helps you make the right decision",
                                "Builder-level technical reasoning",
                                "Reports written for committees and managers",
                            ].map((item) => (
                                <li
                                    key={item}
                                    className="flex items-start gap-4 text-[14.5px] lg:text-[15.5px] text-srm-navy"
                                >
                                    <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold">
                                        <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
                                    </span>
                                    <span className="leading-[1.55]">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="reveal">
                        {submitted ? (
                            <div
                                data-testid="contact-form-success"
                                className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-gold/40 p-10 lg:p-12 shadow-[0_18px_45px_rgba(15,40,86,0.10)]"
                            >
                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[4px] w-full bg-srm-gold" />
                                <span className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-srm-gold/10 ring-1 ring-srm-gold/40 text-srm-gold">
                                    <CheckCircle2 className="h-7 w-7" strokeWidth={1.8} />
                                </span>
                                <h3 className="mt-7 font-display font-bold text-srm-navy text-[1.5rem] lg:text-[1.7rem] tracking-[-0.012em] leading-tight">
                                    Thank you.
                                </h3>
                                <p className="mt-5 text-[15px] lg:text-[15.5px] text-srm-body leading-[1.85]">
                                    Your enquiry has been received by the SRM team.
                                    We will review your information and contact
                                    you shortly.
                                </p>
                                <p className="mt-4 text-[13.5px] lg:text-[14px] text-srm-body/85 leading-[1.75]">
                                    For urgent matters please call{" "}
                                    <a
                                        href={PRIMARY_PHONE_HREF}
                                        className="text-srm-navy font-semibold underline-offset-4 hover:underline"
                                    >
                                        {PRIMARY_PHONE}
                                    </a>{" "}
                                    or email{" "}
                                    <a
                                        href={`mailto:${EMAIL}`}
                                        className="text-srm-navy font-semibold underline-offset-4 hover:underline break-all"
                                    >
                                        {EMAIL}
                                    </a>
                                    .
                                </p>
                            </div>
                        ) : (
                            <form
                                name="srm-enquiry"
                                method="POST"
                                data-netlify="true"
                                data-netlify-honeypot="bot-field"
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-9 lg:p-12 shadow-[0_6px_24px_rgba(15,40,86,0.07)]"
                            >
                                {/* Netlify Forms required hidden fields */}
                                <input type="hidden" name="form-name" value="srm-enquiry" />
                                <p className="hidden" aria-hidden="true">
                                    <label>
                                        Do not fill this out if you are human: <input name="bot-field" tabIndex="-1" autoComplete="off" />
                                    </label>
                                </p>

                                <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold" />

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:gap-6">
                                    <Field label="Name" name="name" required value={form.name} onChange={onChange} />
                                    <Field label="Company / Body Corporate" name="company" value={form.company} onChange={onChange} />
                                    <Field label="Email" name="email" type="email" required value={form.email} onChange={onChange} />
                                    <Field label="Phone" name="phone" type="tel" value={form.phone} onChange={onChange} />
                                </div>

                                <div className="mt-5 lg:mt-6">
                                    <Field label="Property Address" name="address" value={form.address} onChange={onChange} />
                                </div>

                                <div className="mt-5 lg:mt-6">
                                    <label className="font-display font-semibold tracking-[0.16em] uppercase text-srm-navy/65 text-[10.5px]">
                                        Tell us about your property
                                        <span className="text-srm-gold ml-1">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        rows={5}
                                        value={form.message}
                                        onChange={onChange}
                                        aria-label="Tell us about your property"
                                        data-testid="contact-field-message"
                                        className="mt-2 w-full rounded-sm bg-white ring-1 ring-srm-line focus:ring-srm-gold focus:outline-none px-4 py-3 text-[14.5px] text-srm-navy placeholder:text-srm-navy/30 transition-all duration-200 resize-none"
                                        placeholder="Briefly describe your property, the building type and what you'd like SRM to assess."
                                    />
                                </div>

                                <button
                                    type="submit"
                                    data-testid="contact-submit-button"
                                    disabled={submitting}
                                    className="group mt-8 lg:mt-10 inline-flex items-center justify-center gap-2 rounded-sm bg-srm-gold text-white text-[12px] font-semibold tracking-[0.2em] uppercase px-10 h-[56px] border border-srm-gold shadow-[0_10px_30px_rgba(201,165,92,0.30)] hover:bg-srm-goldDark hover:border-srm-goldDark hover:-translate-y-0.5 hover:shadow-[0_14px_36px_rgba(201,165,92,0.40)] transition-all duration-300 w-full sm:w-auto disabled:opacity-60 disabled:hover:translate-y-0"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Send Enquiry
                                            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} />
                                        </>
                                    )}
                                </button>

                                <p className="mt-6 text-[12.5px] text-srm-body/80 leading-[1.6]">
                                    Your enquiry will be securely submitted to SRM and
                                    reviewed by our team. Your details are kept
                                    confidential.
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* Form field helper — premium minimal style */
function Field({ label, name, type = "text", required = false, value, onChange }) {
    return (
        <div>
            <label className="font-display font-semibold tracking-[0.16em] uppercase text-srm-navy/65 text-[10.5px]">
                {label}
                {required && <span className="text-srm-gold ml-1">*</span>}
            </label>
            <input
                name={name}
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                aria-label={label}
                data-testid={`contact-field-${name}`}
                className="mt-2 w-full rounded-sm bg-white ring-1 ring-srm-line focus:ring-srm-gold focus:outline-none px-4 h-[48px] text-[14.5px] text-srm-navy placeholder:text-srm-navy/30 transition-all duration-200"
            />
        </div>
    );
}

/* === 2. Supporting contact info — call, email, service area ======= */
const OPTIONS = [
    {
        id: "call-us",
        icon: Phone,
        eyebrow: "Call Our Team",
        phones: PHONES,
    },
    {
        id: "email-us",
        icon: Mail,
        eyebrow: "Email Us",
        primary: EMAIL,
        secondary: "Your enquiry will be reviewed by the SRM team.",
        action: { label: "Send Email", href: `mailto:${EMAIL}` },
    },
    {
        id: "service-area",
        icon: MapPin,
        eyebrow: "Service Area",
        primary: "Australia Wide",
        secondary: (
            <>
                Based on the Sunshine Coast, Queensland, with consistent independent
                reporting for strata communities nationwide. Read about our{" "}
                <Link
                    to="/sunshine-coast-strata-roof-assessments"
                    data-testid="contact-link-sunshine-coast"
                    className="text-srm-navy font-medium underline underline-offset-4 decoration-srm-gold/60 hover:text-srm-gold transition-colors"
                >
                    independent strata roof assessments on the Sunshine Coast
                </Link>
                .
            </>
        ),
    },
];

function ContactOptions() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="contact-options"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-20 lg:py-28">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 lg:gap-8">
                    {OPTIONS.map((o, i) => {
                        const Icon = o.icon;
                        return (
                            <article
                                key={o.id}
                                id={o.id}
                                data-testid={`contact-option-${i}`}
                                className="reveal group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-srm-line p-9 lg:p-10 shadow-[0_6px_24px_rgba(15,40,86,0.07)] transition-all duration-500 ease-out hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(15,40,86,0.14)] scroll-mt-24"
                                style={{ transitionDelay: `${i * 80}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className="pointer-events-none absolute left-0 top-0 h-[3px] w-16 bg-srm-gold transition-all duration-500 group-hover:w-full"
                                />
                                <span className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full border border-srm-gold/45 text-srm-gold transition-all duration-500 group-hover:bg-srm-gold/10">
                                    <Icon className="h-[26px] w-[26px]" strokeWidth={1.5} />
                                </span>
                                <div className="mt-8 font-display font-bold tracking-[0.22em] uppercase text-srm-navy/65 text-[10.5px] lg:text-[11px]">
                                    {o.eyebrow}
                                </div>
                                <span aria-hidden className="mt-4 block h-[2px] w-10 bg-srm-gold" />

                                {o.phones ? (
                                    <ul className="mt-5 space-y-4 text-srm-navy">
                                        {o.phones.map((p) => (
                                            <li key={p.number}>
                                                <a
                                                    href={`tel:${p.number.replace(/\s+/g, "")}`}
                                                    data-testid={`contact-phone-${p.number.replace(/\s+/g, "")}`}
                                                    className="font-display font-bold text-srm-navy text-[1.05rem] lg:text-[1.12rem] tracking-[-0.005em] leading-none hover:text-srm-gold transition-colors"
                                                >
                                                    {p.number}
                                                </a>
                                                <div className="mt-1.5 text-[12.5px] lg:text-[13px] text-srm-body leading-[1.4]">
                                                    {p.name}
                                                    <span className="text-srm-navy/45"> · {p.role}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <>
                                        <div className="mt-5 font-display font-bold text-srm-navy text-[1.02rem] lg:text-[1.08rem] tracking-[-0.005em] leading-snug break-all">
                                            {o.primary}
                                        </div>
                                        <p className="mt-4 text-[13.5px] lg:text-[14px] text-srm-body leading-[1.7]">
                                            {o.secondary}
                                        </p>
                                    </>
                                )}
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* === 3. Why Contact SRM — expandable cards ======================== */
const WHY = [
    {
        icon: Compass,
        title: "Independent Advice",
        body: "SRM provides independent assessment, reporting and advisory services focused on the interests of the property. Recommendations are based on observed condition, documented evidence and the agreed scope of engagement.",
    },
    {
        icon: HardHat,
        title: "Builder-Level Expertise",
        body: "Trade-qualified assessors who understand how roofs are actually built and how they actually fail. Findings are technical judgements, not brochure interpretations.",
    },
    {
        icon: Users,
        title: "Committee Focus",
        body: "Every report, every conversation and every recommendation is written for the people accountable for the building — committees, strata managers and owners — in language that supports defensible decisions.",
    },
    {
        icon: ClipboardCheck,
        title: "Reporting",
        body: "Structured Guardian Reports\u2122 with date- and zone-referenced photography, prioritised findings and standards-based referencing — the defensible record that holds up long after the assessment is closed.",
    },
];

function WhyContact() {
    const ref = useReveal();
    const [open, setOpen] = useState(0);
    return (
        <section
            ref={ref}
            data-testid="contact-why"
            className="bg-white border-t border-srm-line"
        >
            <div className="container-srm py-20 lg:py-28">
                <div className="max-w-3xl mx-auto text-center reveal">
                    <div className={EYEBROW}>Why Contact SRM</div>
                    <span aria-hidden className={`mx-auto mt-5 ${GOLD_RULE}`} />
                    <h2 className="mt-8 font-display font-bold text-[2rem] sm:text-[2.4rem] lg:text-[2.75rem] tracking-[-0.022em] leading-[1.12] text-srm-navy text-balance">
                        Independent. Technical. Committee-first.
                    </h2>
                </div>

                <ul className="mt-14 lg:mt-16 max-w-4xl mx-auto space-y-3 lg:space-y-4">
                    {WHY.map((w, i) => {
                        const Icon = w.icon;
                        const isOpen = open === i;
                        return (
                            <li
                                key={w.title}
                                data-testid={`why-card-${i}`}
                                className={`reveal group relative overflow-hidden rounded-2xl bg-white ring-1 transition-all duration-500 ease-out ${
                                    isOpen
                                        ? "ring-srm-gold/50 shadow-[0_18px_45px_rgba(15,40,86,0.10)]"
                                        : "ring-srm-line hover:ring-srm-gold/30 shadow-[0_4px_18px_rgba(15,40,86,0.05)]"
                                }`}
                                style={{ transitionDelay: `${i * 60}ms` }}
                            >
                                <span
                                    aria-hidden
                                    className={`pointer-events-none absolute left-0 top-0 h-full w-[3px] bg-srm-gold transition-transform duration-500 origin-top ${
                                        isOpen ? "scale-y-100" : "scale-y-0 group-hover:scale-y-100"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    aria-expanded={isOpen}
                                    aria-controls={`why-panel-${i}`}
                                    data-testid={`why-card-toggle-${i}`}
                                    className="w-full flex items-center gap-5 lg:gap-6 px-7 lg:px-9 py-6 lg:py-7 text-left"
                                >
                                    <span
                                        className={`inline-flex h-[54px] w-[54px] shrink-0 items-center justify-center rounded-full border transition-all duration-500 ${
                                            isOpen
                                                ? "border-srm-gold bg-srm-gold/10 text-srm-gold"
                                                : "border-srm-gold/45 text-srm-gold group-hover:bg-srm-gold/10"
                                        }`}
                                    >
                                        <Icon className="h-[22px] w-[22px]" strokeWidth={1.6} />
                                    </span>
                                    <h3 className="flex-1 font-display font-bold text-srm-navy text-[1.15rem] lg:text-[1.35rem] tracking-[-0.008em] leading-snug">
                                        {w.title}
                                    </h3>
                                    <span
                                        aria-hidden
                                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-all duration-500 ${
                                            isOpen
                                                ? "border-srm-gold bg-srm-gold text-white rotate-180"
                                                : "border-srm-navy/20 text-srm-navy group-hover:border-srm-gold group-hover:text-srm-gold"
                                        }`}
                                    >
                                        {isOpen ? (
                                            <Minus className="h-4 w-4" strokeWidth={2.2} />
                                        ) : (
                                            <Plus className="h-4 w-4" strokeWidth={2.2} />
                                        )}
                                    </span>
                                </button>
                                <div
                                    id={`why-panel-${i}`}
                                    role="region"
                                    aria-hidden={!isOpen}
                                    className={`grid transition-all duration-500 ease-out ${
                                        isOpen
                                            ? "grid-rows-[1fr] opacity-100"
                                            : "grid-rows-[0fr] opacity-0"
                                    }`}
                                >
                                    <div className="overflow-hidden">
                                        <div className="px-7 lg:px-9 pb-8 pl-[6.25rem] lg:pl-[6.75rem]">
                                            <p className="text-[14.5px] lg:text-[15.5px] text-srm-body leading-[1.85] max-w-[640px]">
                                                {w.body}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </section>
    );
}

/* === Page composition ============================================ */
export default function Contact() {
    usePageMeta({
        title: "Contact SRM | Strata Roof Management Australia",
        description:
            "Contact Strata Roof Management Australia (SRM) about an independent strata roof assessment, condition report or ongoing roof asset management. Based on the Sunshine Coast, Queensland, with Australia-wide service.",
        path: "/contact",
        breadcrumbs: [
            { name: "Home", url: "/" },
            { name: "Contact", url: "/contact" },
        ],
    });
    useFAQSchema("contact", FAQ_CONTACT);

    return (
        <div data-testid="contact-page" className="min-h-screen bg-white">
            <Navbar />
            <main id="main">
                <ContactForm />
                <ContactOptions />
                <WhyContact />
            </main>
            <Footer />
        </div>
    );
}
